import { IconPin, IconLock, IconBubble, IconEye } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { StaffRoleIcon } from '@/components/forum/StaffRoleIcon'
import { cn } from '@/lib/cn'
import type { ThreadListItem } from '@/lib/types'

export function ThreadListCard({
  thread,
  isLastRow,
}: {
  thread: ThreadListItem
  isLastRow?: boolean
}) {
  return (
    <article className={cn('bg-white px-4 py-4 md:px-5', !isLastRow && 'border-b border-zinc-100')}>
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            <Link className="font-semibold text-sky-700 no-underline hover:text-zinc-900" to={`/c/${thread.category.slug}`}>
              {thread.category.name}
            </Link>
            <span className="mx-2 text-zinc-400" aria-hidden="true">
              ·
            </span>
            <span className="inline-flex flex-wrap items-center gap-x-1 font-semibold tracking-normal normal-case text-zinc-600">
              By{' '}
              <Link className="text-zinc-900 no-underline hover:text-sky-700" to={`/u/${thread.author.username}`}>
                {thread.author.username}
              </Link>

              <StaffRoleIcon role={thread.author.role} />
            </span>
          </p>

          <h2 className="mt-1 text-[17px] font-semibold leading-snug tracking-tight text-zinc-950 md:text-[18px]">
            <Link className="text-zinc-950 no-underline hover:text-sky-700" to={`/t/${thread.slug}`}>
              {thread.title}
              {thread.pinned ? (
                <span className="ml-1 align-middle text-amber-900" aria-label="Pinned" title="Pinned">
                  <IconPin className="inline-block size-5" aria-hidden="true" />
                </span>
              ) : null}
              {thread.locked ? (
                <span className="ml-1 align-middle text-nk-navy" aria-label="Locked" title="Locked">
                  <IconLock className="inline-block size-5" aria-hidden="true" />
                </span>
              ) : null}
            </Link>
          </h2>
        </div>

        <div className="min-w-[8rem] shrink-0 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Activity</p>
          <time className="block text-[12px] font-semibold tabular-nums text-zinc-900" dateTime={new Date(thread.lastActivityAt * 1000).toISOString()}>
            {formatRelative(thread.lastActivityAt)}
          </time>
        </div>
      </header>

      <div className="mt-1.5">
        {thread.excerpt ? (
          <p className="line-clamp-2 text-[14px] leading-relaxed text-zinc-700">{thread.excerpt}</p>
        ) : (
          <p className="text-[14px] italic text-zinc-500">No preview excerpt.</p>
        )}
      </div>

      <footer className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 pt-3 text-[12px] font-semibold">
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1 tabular-nums text-zinc-600">
            <IconBubble className="size-4 text-zinc-900" aria-hidden="true" />
            <span>{thread.replyCount}</span> replies
          </span>

          <span className="inline-flex items-center gap-1 tabular-nums text-zinc-600">
            <IconEye className="size-4 text-zinc-900" aria-hidden="true" />
            <span>{thread.viewCount}</span> views
          </span>
        </div>

        <span
          className={cn(
            'rounded-md border border-zinc-200 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-600',
            thread.status !== 'open' ? 'opacity-70' : null,
          )}
        >
          {thread.status}
        </span>
      </footer>
    </article>
  )
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts * 1000
  const mins = Math.round(diff / 60000)
  if (mins < 60) return `${Math.max(1, mins)}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 48) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  return `${days}d ago`
}
